/**
 * v2 navbar — Home / Locations / Jobs + Tools ▾ + Settings ▾ + Alarm
 * bell + user.
 *
 * Replaces v1's flat 11-link bar. The Tools and Settings groups hide
 * indirect / admin pages so the eye doesn't have to scan past them
 * when looking for a chassis to triage. Jobs (with its Operator |
 * Automation views) is checked often enough that v0.34.0 promoted it
 * to a top-level link; ONU lookup + MAC map left the Tools menu (the
 * global omnisearch is the entry point — their routes stay alive).
 *
 * Alarm bell: badge count comes from ``GET /api/alarms/count``;
 * clicking the bell navigates to ``/v2/alarms`` for the cross-
 * chassis triage view.
 *
 * Radix's headless primitives drive the dropdown menus — they handle
 * keyboard navigation (Arrow keys, Esc, Enter), focus trapping, and
 * click-outside-to-close.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  ChevronDown,
  HelpCircle,
  LogOut,
  Network,
  Settings as SettingsIcon,
  Wrench,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { api } from "../../api/client";
import { useLogout, useSession } from "../../auth/SessionContext";
import { Button } from "../components/Button";
import { GlobalSearch } from "../components/GlobalSearch";

type AlarmCount = { unacknowledged: number };

type NavLink = {
  label: string;
  to: string;
  description?: string;
};

const TOOLS_LINKS: NavLink[] = [
  { label: "Bulk jobs", to: "/tools/bulk-jobs",
    description: "Fan-out a job across many chassis at once." },
  { label: "Terminal sessions", to: "/tools/terminal-sessions",
    description: "Live + historical SSH session log." },
  { label: "Locks", to: "/tools/locks",
    description: "Active chassis locks (who's holding what)." },
];

const SETTINGS_LINKS: NavLink[] = [
  { label: "Templates", to: "/settings/templates",
    description: "Per-vendor command templates + version history." },
  { label: "Schedules", to: "/settings/schedules",
    description: "Recurring jobs (cron / interval)." },
  { label: "Audit log", to: "/settings/audit",
    description: "Every operator action with actor + outcome." },
  { label: "Alarm rules", to: "/settings/alarm-rules",
    description: "Pattern → severity mappings for the alarm bell." },
  { label: "My settings", to: "/settings/me",
    description: "Theme, cache, session info, legacy-UI fallback." },
];

function NavMenu({
  label,
  icon,
  links,
}: {
  label: string;
  icon: React.ReactNode;
  links: NavLink[];
}) {
  const location = useLocation();
  // Highlight the trigger if any descendant route is active.
  const isActive = links.some((l) => location.pathname.startsWith(l.to));
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={[
          "group inline-flex items-center gap-1 rounded px-2 py-1 text-sm",
          "text-slate-300 hover:text-slate-100 hover:bg-slate-800",
          "transition-[scale,background-color,color] duration-150 ease-out",
          "active:scale-[0.96]",
          "dark:text-slate-300 dark:hover:text-white",
          isActive ? "text-slate-100" : "",
        ].join(" ")}
        aria-label={`${label} menu`}
      >
        {icon}
        <span>{label}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70 transition-transform duration-150 ease-out group-data-[state=open]:rotate-180" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          // Concentric radius: p-1 (4px) + rounded (4px) items → rounded-lg
          // (8px) container. Keeps its edge border (the menu's defining ring)
          // plus shadow-lg for elevation.
          className={[
            "z-50 min-w-[18rem] rounded-lg border p-1 shadow-lg",
            "border-slate-700 bg-slate-900 text-slate-200",
            "dark:border-slate-700 dark:bg-slate-900",
          ].join(" ")}
          sideOffset={6}
          align="start"
        >
          {links.map((link) => (
            <DropdownMenu.Item
              key={link.to}
              asChild
              // Radix handles keyboard nav; styling via className.
              className={[
                "block cursor-pointer rounded px-2 py-1.5 text-sm outline-none",
                "transition-[background-color,color] duration-150 ease-out",
                "data-[highlighted]:bg-slate-800 data-[highlighted]:text-white",
              ].join(" ")}
            >
              <Link to={link.to}>
                <div className="font-medium">{link.label}</div>
                {link.description && (
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {link.description}
                  </div>
                )}
              </Link>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function AlarmBell() {
  const q = useQuery<AlarmCount>({
    queryKey: ["v2", "alarm-count"],
    queryFn: async () => {
      try {
        const res = await api.get<AlarmCount>("/alarms/count");
        return res.data;
      } catch {
        return { unacknowledged: 0 };
      }
    },
    refetchInterval: 30_000,
    retry: 0,
  });
  const count = q.data?.unacknowledged ?? 0;
  return (
    <Link
      to="/alarms"
      className={[
        "relative inline-flex items-center rounded px-2 py-1",
        "text-slate-300 hover:bg-slate-800 hover:text-slate-100",
        "transition-[scale,background-color,color] duration-150 ease-out",
        "active:scale-[0.96]",
      ].join(" ")}
      aria-label={`${count} unacknowledged alarms`}
      title={`${count} unacknowledged alarms — click to open the all-alarms page`}
    >
      <Bell className="h-4 w-4" />
      {count > 0 && (
        <span
          className={[
            "absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem]",
            "items-center justify-center rounded-full px-1 text-[10px]",
            "font-medium tabular-nums text-white",
            count > 9 ? "bg-red-600" : "bg-amber-600",
          ].join(" ")}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

/** Shown only while a VPN tunnel is up or connecting — keeps the bar
 *  uncluttered when the optional VPN isn't in use. */
function VpnStatus() {
  const q = useQuery<{ state: string; detail?: string | null }>({
    queryKey: ["v2", "vpn-status-nav"],
    queryFn: async () => {
      try {
        return (
          await api.get<{ state: string; detail?: string | null }>(
            "/vpn/status",
          )
        ).data;
      } catch {
        return { state: "unknown" };
      }
    },
    refetchInterval: 30_000,
    retry: 0,
  });
  const state = q.data?.state ?? "unknown";
  if (state !== "up" && state !== "connecting" && state !== "error")
    return null;
  const tone =
    state === "up"
      ? "text-emerald-300"
      : state === "error"
        ? "text-red-400"
        : "text-amber-300";
  return (
    <Link
      to="/settings/vpn"
      title={`VPN ${state}${q.data?.detail ? `: ${q.data.detail}` : ""} — click to manage`}
      className={[
        "inline-flex items-center gap-1 rounded px-2 py-1 text-xs",
        "transition-[scale,background-color,color] duration-150 ease-out",
        "hover:bg-slate-800 active:scale-[0.96]",
        tone,
      ].join(" ")}
    >
      <Network className="h-4 w-4" />
      <span className="hidden sm:inline">VPN</span>
    </Link>
  );
}

export function Navbar() {
  const { user } = useSession();
  const logout = useLogout();
  // API Keys management is admin-only; only surface the link for users in
  // the CHASSIS_API_KEY_ADMINS allowlist (the page itself also 403s).
  const settingsLinks: NavLink[] = user?.is_api_key_admin
    ? [
        ...SETTINGS_LINKS.slice(0, -1),
        {
          label: "API keys",
          to: "/settings/api-keys",
          description: "Issue / revoke keys for the external lookup API.",
        },
        {
          label: "VPN",
          to: "/settings/vpn",
          description: "OpenVPN client for off-net access.",
        },
        {
          label: "Access control",
          to: "/settings/access",
          description: "IP allowlist for the external API / whole site.",
        },
        SETTINGS_LINKS[SETTINGS_LINKS.length - 1],
      ]
    : SETTINGS_LINKS;
  return (
    <header
      className={[
        "border-b border-slate-800 bg-slate-900 px-6 py-3",
        "dark:border-slate-800 dark:bg-slate-950",
      ].join(" ")}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <div className="flex items-center gap-5">
          <Link
            to="/"
            className="flex items-center gap-2 tracking-tight text-slate-100"
          >
            <img
              src="/bm-logo-small.png"
              alt="Gigafy Chassis"
              className="h-7 w-auto rounded outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
            />
            <span className="rounded border border-emerald-700 bg-emerald-900/30 px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-emerald-300">
              v2
            </span>
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link
              to="/"
              className="rounded px-2 py-1 text-slate-300 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
            >
              Home
            </Link>
            <Link
              to="/locations"
              className="rounded px-2 py-1 text-slate-300 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
            >
              Locations
            </Link>
            <Link
              to="/settings/jobs"
              className="rounded px-2 py-1 text-slate-300 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
            >
              Jobs
            </Link>
            <NavMenu
              label="Tools"
              icon={<Wrench className="h-3.5 w-3.5" />}
              links={TOOLS_LINKS}
            />
            <NavMenu
              label="Settings"
              icon={<SettingsIcon className="h-3.5 w-3.5" />}
              links={settingsLinks}
            />
          </nav>
        </div>
        {/* Global omnisearch — "/" focuses it from anywhere. */}
        <div className="hidden min-w-0 flex-1 justify-center px-2 sm:flex">
          <GlobalSearch />
        </div>
        <div className="flex items-center gap-3">
          <VpnStatus />
          <Link
            to="/help"
            title="Help & FAQ"
            className="inline-flex items-center rounded px-2 py-1 text-slate-400 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
          >
            <HelpCircle className="h-4 w-4" />
          </Link>
          <AlarmBell />
          {user && (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <span>{user.gigafy_username}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => logout.mutate()}
                title="Log out"
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
